import { filledFilterValues } from "@fortex/api";
import type { ForteXApi, MapObject, TileFilterCapped, TileFilterField, TileFilterValues } from "@fortex/api";
import { mapObjectFromFeature } from "../features/adapters";
import type { Labels } from "../i18n/labels";
import type { LayerManager } from "../layers/LayerManager";
import type { SearchApi } from "./api";
import type { BoundsTuple, ResolvedOptions } from "./options";
import type { PersistedState } from "./persisted";
import type { AddressHit, MapStore, ResultItem, ResultsState } from "./store";
import { debounce, errorMessage, isAbort } from "./support";
import type { ViewController } from "./view";

export const searchZoom = 16;

export const recentLimit = 8;

export const minimumTermLength = 4;

export const searchDelayMs = 250;

type FeatureResults = {
  items: ResultItem[];
  capped: TileFilterCapped[];
};

export type SearchControllerOptions = {
  api: ForteXApi;
  store: MapStore;
  labels: Labels;
  options: ResolvedOptions["controls"]["search"];
  layers: LayerManager;
  view: ViewController;
  admin?: () => boolean;
  onPickObject(object: MapObject): void;
  persisted: PersistedState;
  savePersisted(patch: Partial<PersistedState>): void;
};

export type SearchController = SearchApi & { dispose(): void };

export function searchLabelOf(entries: { selected: boolean; kind: string; idName: string; searchLabel: string | null }[], allowed: string[] | null, override: string | null): string | null {
  if (override !== null && override.trim().length > 0) return override.trim();

  const labels = entries
    .filter(a => a.selected && a.kind === "folder" && (allowed === null || allowed.includes(a.idName)))
    .map(a => (a.searchLabel ?? "").trim())
    .filter(a => a.length > 0);
  const unique = [...new Set(labels)];

  return unique.length === 0 ? null : unique.join(", ");
}

export function searchableFolderLayers(layers: LayerManager, allowed: string[] | null = null): string[] {
  return layers
    .registry.list()
    .filter(a => a.selected && a.entry.kind === "folder")
    .map(a => a.entry.idName)
    .filter(idName => allowed === null || allowed.includes(idName));
}

export function activeFilterValues(fields: TileFilterField[], filters: TileFilterValues): TileFilterValues {
  const known: TileFilterValues = {};

  for (const field of fields) {
    const value = filters[field.layer]?.[field.key];

    if (value !== undefined) known[field.layer] = { ...(known[field.layer] ?? {}), [field.key]: value };
  }

  return filledFilterValues(known);
}

export function createSearchController(settings: SearchControllerOptions): SearchController {
  const { api, store, labels, layers, view } = settings;
  let controller: AbortController | null = null;
  let featureController: AbortController | null = null;
  let fieldsController: AbortController | null = null;
  let fieldLayers = "";
  let run = 0;
  let disposed = false;

  const patch = (partial: Partial<MapStore extends { getState(): infer S } ? (S extends { search: infer T } ? T : never) : never>) => {
    if (disposed) return;

    store.setState({ search: { ...store.getState().search, ...partial } });
  };

  const patchResults = (partial: Partial<ResultsState>) => {
    if (disposed) return;

    store.setState({ results: { ...store.getState().results, ...partial } });
  };

  const clearSearchResults = () => {
    if (store.getState().results.source === "search") patchResults({ source: null, term: "", items: [], capped: [], loading: false, error: null });
  };

  const abort = () => {
    controller?.abort();
    controller = null;
    featureController?.abort();
    featureController = null;
  };

  const activeFilters = () => activeFilterValues(store.getState().search.filterFields, store.getState().search.filters);

  const objects = async (term: string, take = 20): Promise<MapObject[]> => {
    const trimmed = term.trim();

    if (trimmed.length < minimumTermLength) return [];

    controller?.abort();
    controller = new AbortController();

    const signal = controller.signal;
    const sources = layers.selectedSources().filter(a => a.features?.search);
    const searchable = sources.length > 0 ? sources : layers.registry.list().map(a => a.source).filter(a => a.features?.search);
    const seen = new Set<number>();
    const result: MapObject[] = [];

    if (searchable.length === 0) {
      const typeIds = layers
        .catalogueEntries()
        .filter(a => a.kind === "geoobject" && a.root === "layers")
        .map(a => a.idName);

      if (typeIds.length === 0) return [];

      const list = await api.mapObjects({ typeIds, bounds: null, zoomLevel: null, group: false, search: trimmed, take }, signal);

      return list.entities;
    }

    const batches = await Promise.all(searchable.map(a => a.features!.search!(trimmed, take, signal).catch(() => [])));

    for (const features of batches) {
      for (const feature of features) {
        const object = mapObjectFromFeature(feature);

        if (object === null || seen.has(object.id)) continue;

        seen.add(object.id);
        result.push(object);
      }
    }

    return result.slice(0, take);
  };

  const features = async (term: string, filters: TileFilterValues): Promise<FeatureResults> => {
    const trimmed = term.trim();
    const searchable = searchableFolderLayers(layers, settings.options.layers);
    const layerNames = trimmed.length >= minimumTermLength ? searchable : searchable.filter(a => filters[a] !== undefined);

    if (layerNames.length === 0) return { items: [], capped: [] };

    featureController?.abort();
    featureController = new AbortController();

    const sent = Object.fromEntries(Object.entries(filters).filter(([layer]) => layerNames.includes(layer)));
    const result = await api.filter(trimmed, layerNames, "", settings.admin ? settings.admin() : api.authenticated, featureController.signal, sent);
    const items: ResultItem[] = result.hits.map(a => ({ kind: "feature", hit: { id: a.id, layer: a.layer ?? layerNames[0], name: a.name ?? "", feature: null }, latlng: null, zoom: null }));

    return { items, capped: result.capped };
  };

  const addresses = async (term: string): Promise<AddressHit[]> => {
    const hits = await api.searchAddress(term.trim());

    return hits.map(a => ({ displayName: a.displayName, lat: a.lat, lon: a.lon, boundingBox: a.boundingBox }));
  };

  const runSearch = async (term: string) => {
    const trimmed = term.trim();
    const filters = activeFilters();
    const longEnough = trimmed.length >= minimumTermLength;

    if (!longEnough && Object.keys(filters).length === 0) return;

    const current = ++run;
    let failure: string | null = null;
    const guard =
      <T,>(fallback: T) =>
      (error: unknown): T => {
        if (!isAbort(error)) failure ??= errorMessage(error, labels);

        return fallback;
      };

    patch({ loading: true, error: null });
    patchResults({ source: "search", term: trimmed, capped: [], loading: true, error: null });

    if (!disposed) store.setState({ sideTab: "results" });

    const [objectsFound, featuresFound, addressesFound] = await Promise.all([
      longEnough && settings.options.objects ? objects(trimmed).catch(guard<MapObject[]>([])) : Promise.resolve([] as MapObject[]),
      features(trimmed, filters).catch(guard<FeatureResults>({ items: [], capped: [] })),
      longEnough && settings.options.address ? addresses(trimmed).catch(guard<AddressHit[]>([])) : Promise.resolve([] as AddressHit[])
    ]);

    if (disposed || current !== run || store.getState().search.term.trim() !== trimmed) return;

    const items: ResultItem[] = [...objectsFound.map(object => ({ kind: "object", object }) as ResultItem), ...featuresFound.items, ...addressesFound.map(hit => ({ kind: "address", hit }) as ResultItem)];

    patch({ objects: objectsFound, addresses: addressesFound, loading: false, error: failure });
    patchResults({ source: "search", term: trimmed, items, capped: featuresFound.capped, loading: false, error: failure });
  };

  const scheduled = debounce(searchDelayMs, (term: string) => void runSearch(term));

  const stop = () => {
    run++;
    scheduled.cancel();
    abort();
    patch({ objects: [], addresses: [], loading: false, error: null });
    clearSearchResults();
  };

  const refresh = () => {
    const term = store.getState().search.term;

    if (term.trim().length < minimumTermLength && Object.keys(activeFilters()).length === 0) {
      stop();
      return;
    }

    scheduled.call(term);
  };

  const applyFilterFields = (fields: TileFilterField[]) => {
    if (disposed) return;

    const before = JSON.stringify(activeFilters());

    patch({ filterFields: fields });

    if (JSON.stringify(activeFilters()) !== before) refresh();
  };

  const syncFilterFields = () => {
    if (disposed || !settings.options.enabled) return;

    const names = searchableFolderLayers(layers, settings.options.layers);
    const key = names.join(",");

    if (key === fieldLayers) return;

    fieldLayers = key;
    fieldsController?.abort();
    fieldsController = null;

    if (names.length === 0) {
      applyFilterFields([]);
      return;
    }

    const current = new AbortController();

    fieldsController = current;
    api
      .filterFields(names, current.signal)
      .then(fields => {
        if (!current.signal.aborted) applyFilterFields(fields);
      })
      .catch(() => {
        if (!current.signal.aborted) applyFilterFields([]);
      });
  };

  const unsubscribeLayers = store.subscribe((state, previous) => {
    if (state.layers !== previous.layers) syncFilterFields();
  });

  syncFilterFields();

  const address = async (term: string) => {
    const trimmed = term.trim();

    if (trimmed.length < minimumTermLength) return [];

    patch({ loading: true, error: null });

    try {
      const mapped = await addresses(trimmed);

      patch({ addresses: mapped, loading: false });

      return mapped;
    } catch (error) {
      patch({ loading: false, error: errorMessage(error, labels), addresses: [] });

      return [];
    }
  };

  const remember = (object: MapObject) => {
    const recent = [object, ...store.getState().search.recent.filter(a => a.id !== object.id)].slice(0, recentLimit);

    patch({ recent });
    settings.persisted.recentSearches = recent.map(a => a.id);
    settings.savePersisted({ recentSearches: settings.persisted.recentSearches });
  };

  return {
    setTerm: term => {
      patch({ term });
      refresh();
    },
    setFilter: (layer, key, value) => {
      const filters = store.getState().search.filters;

      patch({ filters: { ...filters, [layer]: { ...(filters[layer] ?? {}), [key]: value } } });
      refresh();
    },
    setTab: tab => {
      patch({ tab });
    },
    objects,
    address,
    pickObject: object => {
      remember(object);
      view.setView([object.lat, object.lon], Math.max(view.getZoom(), searchZoom));
      settings.onPickObject(object);
    },
    pickAddress: hit => {
      if (hit.boundingBox) view.fitBounds(hit.boundingBox as BoundsTuple, { maxZoom: searchZoom });
      else view.setView([hit.lat, hit.lon], searchZoom);
    },
    clear: () => {
      patch({ term: "" });
      refresh();
    },
    dispose: () => {
      disposed = true;
      unsubscribeLayers();
      scheduled.cancel();
      abort();
      fieldsController?.abort();
      fieldsController = null;
    }
  };
}
